import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingBackgroundType } from '../../constants/asset'
import { useThemeAssets } from '../../hooks/useThemeAssets'
import { useThemeFeatures } from '../../hooks/useThemeFeatures'

interface LoadingScreenProps {
  backgroundType?: LoadingBackgroundType | 'random'
}

// Helper function to get random background
const getRandomBackground = (): LoadingBackgroundType => {
  const backgrounds: LoadingBackgroundType[] = ['bg1', 'bg2', 'bg3']
  return backgrounds[Math.floor(Math.random() * backgrounds.length)]
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({
  backgroundType = 'bg1',
}) => {
  const { t } = useTranslation()
  const { loadingAssets } = useThemeAssets()
  const [progress, setProgress] = useState(0)
  const themeFeatures = useThemeFeatures()

  // Select random background once on mount (prevents glitching during loading)
  const [selectedBg] = useState<LoadingBackgroundType>(() =>
    backgroundType === 'random' ? getRandomBackground() : backgroundType,
  )
  const backgroundImage = loadingAssets.backgrounds[selectedBg]

  // Simulate loading progress
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + 2 // Increment by 2 for faster progress

        if (newProgress >= 100) {
          clearInterval(timer)
          return 100
        }

        return newProgress
      })
    }, 2) // 2ms * 50 steps = 100ms total (super fast!)

    return () => clearInterval(timer)
  }, [])

  return (
    <div
      className="absolute inset-0 w-full h-full min-h-full flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      {/* Overlay for better contrast */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-[var(--loading-screen-overlay-start)]/10 via-transparent to-[var(--loading-screen-overlay-end)]/20"></div>

      {/* Content Container - Conditionally positioned */}
      <div
        className={`z-10 flex flex-col items-center text-center px-6 w-full mx-auto ${
          themeFeatures.loadingScreenTitle
            ? 'absolute bottom-32 left-0 right-0'
            : 'relative justify-center'
        }`}
      >
        {/* Main Title - "Online Lotto Number 1" */}

        <div className={themeFeatures.loadingScreenTitle ? 'hidden' : 'w-full'}>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-poppins">
            <span className="text-[var(--loading-screen-title-text)] font-poppins font-bold">
              {t('loadingScreen.title.onlineLotto', 'Online Lotto')}
            </span>
            <span className="text-[var(--loading-screen-title-text)]"> </span>
            <span className="bg-gradient-to-r from-[var(--loading-title-start)] via-[var(--loading-title-mid)] to-[var(--loading-title-end)] bg-clip-text text-transparent font-poppins font-bold">
              {t('loadingScreen.title.numberOne', 'Number 1')}
            </span>
          </h1>

          {/* Subtitle - "Safety and Bonus" */}
          <h4 className="text-xl sm:text-2xl md:text-3xl text-[var(--loading-screen-subtitle-text)] tracking-wide">
            {t('loadingScreen.subtitle', 'Safety and Bonus')}
          </h4>
        </div>

        {/* Loading Text and Percentage */}
        <div
          className={`w-full max-w-3xl mx-auto px-6 gap-2 mb-4
            ${
              themeFeatures.loadingScreenTitle
                ? 'flex justify-between items-center'
                : 'flex flex-col items-center'
            }`}
        >
          <p className="text-base sm:text-lg font-medium text-[var(--loading-screen-loading-text)]">
            {t('common.loading', 'Loading...')}
          </p>
          <span className="text-sm text-[var(--loading-screen-percentage-text)]/60">
            {progress}%
          </span>
        </div>

        {/* Progress Bar Container */}
        <div className="w-full max-w-3xl mx-auto px-6">
          <div className="relative">
            {/* Progress Bar Background with Border */}
            <div className="w-full h-12 sm:h-16 md:h-16 rounded-full border-4 shadow-2xl relative overflow-hidden p-2 border-[var(--loading-screen-progress-border)]">
              {/* Progress Fill */}
              <div
                className="h-full rounded-full transition-all duration-300 ease-out active-gradient"
                style={{
                  width: `${progress}%`,
                }}
              >
                {/* Inner glow effect */}
                <div className="h-full w-full rounded-full bg-gradient-to-r from-transparent via-[var(--loading-screen-glow)]/30 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoadingScreen
